import * as React from 'react';
import { DndContext, useSensor, useSensors, PointerSensor, TouchSensor, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import { darken, useTheme } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import ReactQuill from 'react-quill-new';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import * as Device from 'react-device-detect';

import 'react-quill-new/dist/quill.snow.css';
import './quill.css';

import { useSession } from '@hooks';
import {
  useGetEventQuery,
  GetEventQuery,
  useUpsertEventFaqMutation,
  useRemoveEventFaqMutation,
  useSortEventFaqMutation,
} from '@graphql';
import { faqValidationSchema } from './validationSchema';
import { upperFirst } from 'lodash';

interface EventFAQFormDialogProps {}

export function EventFAQFormDialog(props: Readonly<EventFAQFormDialogProps>) {
  const { account } = useSession();
  const [isOpen, setIsOpen] = React.useState(false);
  const { eventId } = useParams();
  const { state } = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('field');

  const { data, refetch } = useGetEventQuery({
    variables: { id: eventId || '' },
    skip: !account || !eventId,
    onCompleted({ event }) {
      setIsOpen(true);
    },
  });

  const handleClose = () => {
    setIsOpen(false);
    navigate(state.from || -1, { preventScrollReset: true, replace: true });
  };

  if (!data) {
    return null;
  }

  const { id, title, FAQs } = data.event;

  return (
    <Dialog
      open={isOpen}
      scroll="body"
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={Device.isMobileOnly}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, .6)',
          },
        },
        paper: {
          sx: {
            backgroundColor: 'theme.palette.background.default',
            border: Device.isMobile ? undefined : '1px solid rgba(255, 255, 255, 0.1)',
            pt: 'env(safe-area-inset-top)',
            minHeight: '90vh',
          },
        },
      }}
      slots={{
        transition: Transition,
      }}
    >
      <Stack height="100%" sx={{ position: 'relative' }} justifyContent="space-between" px={3} pt={3}>
        <Stack spacing={2} height="100%">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('event.title.edit-faq')}</Typography>
            <IconButton onClick={handleClose}>
              <theme.icons.close />
            </IconButton>
          </Stack>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 1,
              backgroundColor: darken(theme.palette.background.paper, 0.5),
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {t('word.event_one', { ns: 'common' }).toUpperCase()}
            </Typography>
            <Typography variant="body2">{title}</Typography>
          </Paper>
          <SortableFAQ eventId={id} items={FAQs} refetchEvent={refetch} />
        </Stack>
      </Stack>
    </Dialog>
  );
}

interface SortableFAQProps {
  eventId: string;
  items: GetEventQuery['event']['FAQs'];
  onChange?: (next: GetEventQuery['event']['FAQs']) => void;
  refetchEvent: () => void;
}

function SortableFAQ(props: Readonly<SortableFAQProps>) {
  const { eventId, items, onChange, refetchEvent } = props;
  const [state, setState] = React.useState(items);
  const [isAddItem, setIsAddItem] = React.useState(false);
  const [faqToUpdate, setFaqToUpdate] = React.useState<GetEventQuery['event']['FAQs'][number] | null>(null);
  const theme = useTheme();
  const { t } = useTranslation('cta');

  const disabled = state.length <= 1;

  const [sortEventFAQ] = useSortEventFaqMutation({
    onError(error) {
      toast.error(t('event.toast.sortFAQ.error', { ns: 'field' }));
    },
  });

  // Keep local state in sync when parent items change
  React.useEffect(() => {
    setState([...items].sort((a, b) => a.idx - b.idx));
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const onDragEnd = async (e: DragEndEvent) => {
    if (disabled) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;

    // ✅ Reorder and reindex idx
    const prev = state; // keep a snapshot for rollback
    const oldIndex = prev.findIndex((i) => i.id === active.id);
    const newIndex = prev.findIndex((i) => i.id === over.id);

    // Reorder locally
    const next = arrayMove(prev, oldIndex, newIndex).map((it, idx) => ({ ...it, idx }));
    const prevOrder = prev.map((i) => i.id);
    const nextOrder = next.map((i) => i.id);

    // If order didn't actually change, skip network
    if (prevOrder.join('|') === nextOrder.join('|')) return;

    try {
      await sortEventFAQ({ variables: { eventId, order: nextOrder } });
      setState(next);
      onChange?.(next); // ✅ same GraphQL shape back to caller
    } catch (error) {
      // Rollback on failure
    }
  };

  if (isAddItem) {
    return (
      <FaqForm eventId={eventId} isOpen={isAddItem} setIsOpen={setIsAddItem} refetchEvent={refetchEvent} />
    );
  }

  if (faqToUpdate) {
    return (
      <FaqForm
        eventId={eventId}
        isOpen={true}
        setIsOpen={() => setFaqToUpdate(null)}
        faq={faqToUpdate}
        refetchEvent={refetchEvent}
      />
    );
  }

  if (disabled) {
    return (
      <Stack spacing={2} mt={2}>
        <Button
          variant="contained"
          color="uncolored"
          startIcon={<theme.icons.add />}
          size={Device.isMobile ? 'small' : 'medium'}
          onClick={setIsAddItem}
          sx={{ display: isAddItem ? 'none' : 'flex' }}
        >
          {t('add', { ns: 'cta' })}
        </Button>
        {state.map((item) => (
          <SortableFAQRow
            key={item.id}
            item={item}
            eventId={eventId}
            disableDrag
            refetchEvent={refetchEvent}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} mt={2}>
      <Button
        variant="contained"
        color="uncolored"
        startIcon={<theme.icons.add />}
        size={Device.isMobile ? 'small' : 'medium'}
        onClick={setIsAddItem}
        sx={{ display: isAddItem ? 'none' : 'flex' }}
      >
        {t('add', { ns: 'cta' })}
      </Button>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext items={state.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {state.map((item) => (
            <SortableFAQRow
              key={item.id}
              item={item}
              eventId={eventId}
              refetchEvent={refetchEvent}
              setFaqToUpdate={setFaqToUpdate}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Stack>
  );
}

interface SortableFAQRowProps {
  eventId: string;
  item: GetEventQuery['event']['FAQs'][number];
  disableDrag?: boolean;
  refetchEvent: () => void;
  setFaqToUpdate?: (faq: GetEventQuery['event']['FAQs'][number] | null) => void;
}

// Single row component with drag handle (GraphQL shape: { id, idx, question, answer })
function SortableFAQRow(props: SortableFAQRowProps) {
  const { eventId, item, disableDrag, refetchEvent, setFaqToUpdate } = props;
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const [isConfirmDelete, setIsConfirmDelete] = React.useState(false);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const theme = useTheme();
  const { t } = useTranslation('cta');

  const iconSize = { xs: '1rem', sm: '1.2rem' };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [removeFAQ, { loading: removing }] = useRemoveEventFaqMutation({
    variables: {
      id: item.id,
    },
    onCompleted: ({ removeEventFAQ }) => {
      toast.success(t('event.toast.removeFAQ.success', { ns: 'field' }));
      setIsConfirmDelete(false);
      refetchEvent();
    },
    onError(error) {
      toast.error(t('event.toast.removeFAQ.error', { ns: 'field' }));
      setIsConfirmDelete(false);
    },
  });

  const handleUpdate = () => {
    setFaqToUpdate && setFaqToUpdate(item);
  };

  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        border: '1px solid rgba(255,255,255,0.1)',
        touchAction: 'none',
        userSelect: 'none',
        backgroundColor: 'action.selected',
      }}
      style={style}
    >
      <Stack spacing={1}>
        <Typography variant="body2" fontWeight="bold" fontSize={{ xs: '0.9rem', sm: '1rem' }}>
          {item.question}
        </Typography>

        {/* If `answer` is HTML, swap the block below for a sanitized `dangerouslySetInnerHTML` */}
        <Collapse in={isPreviewing} timeout="auto" unmountOnExit>
          <Typography
            variant="body2"
            sx={{ mt: 0.5 }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer) }}
          />
        </Collapse>
        <Divider />

        {isConfirmDelete ? (
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} m={0} p={0}>
            <Button variant="outlined" size="small" color="error" loading={removing} onClick={removeFAQ}>
              {t('confirm')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="uncolored"
              onClick={() => setIsConfirmDelete((v) => !v)}
              disabled={removing}
            >
              {t('cancel')}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} m={0} p={0}>
            {/* Drag handle: keep it simple and mobile-friendly */}
            {!disableDrag && (
              <IconButton
                aria-label="move"
                {...attributes}
                {...listeners}
                sx={{ cursor: 'grab', touchAction: 'none' }}
              >
                <theme.icons.move sx={{ fontSize: iconSize }} />
              </IconButton>
            )}
            <IconButton
              aria-label="preview"
              aria-expanded={isPreviewing}
              onClick={() => setIsPreviewing((v) => !v)}
            >
              {isPreviewing ? (
                <theme.icons.expandLess sx={{ fontSize: iconSize }} />
              ) : (
                <theme.icons.expandMore sx={{ fontSize: iconSize }} />
              )}
            </IconButton>
            <IconButton aria-label="edit" onClick={handleUpdate}>
              <theme.icons.edit sx={{ fontSize: iconSize }} />
            </IconButton>
            <IconButton
              aria-label="delete"
              aria-expanded={isPreviewing}
              onClick={() => setIsConfirmDelete((v) => !v)}
            >
              <theme.icons.delete sx={{ fontSize: iconSize }} />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

interface FaqFormProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  eventId: string;
  faq?: GetEventQuery['event']['FAQs'][number];
  refetchEvent: () => void;
}

const FaqForm = (props: FaqFormProps) => {
  const { eventId, isOpen, setIsOpen, faq, refetchEvent } = props;
  const { t } = useTranslation('field');
  const toastKey = faq ? 'updateFAQ' : 'addFAQ';

  const [upsertFAQ, { loading }] = useUpsertEventFaqMutation({
    onCompleted: ({ upsertEventFAQ }) => {
      toast.success(t(`event.toast.${toastKey}.success`, { ns: 'field' }));
      refetchEvent();
      handleClose();
    },
    onError(error) {
      toast.error(t(`event.toast.${toastKey}.error`, { ns: 'field' }));
      handleClose();
    },
  });

  const formik = useFormik({
    initialValues: {
      question: faq?.question || '',
      answer: faq?.answer || '<p><br></p>',
    },
    validationSchema: faqValidationSchema(t),
    onSubmit: (values) => {
      console.log(`[values]:`, values);
      upsertFAQ({
        variables: {
          input: {
            eventId,
            id: faq?.id,
            question: values.question,
            answer: values.answer,
          },
        },
      });
    },
  });

  const handleClose = () => {
    setIsOpen(false);
    formik.resetForm();
  };

  React.useEffect(() => {
    // seek for the h3 button
    const h3Button = document.querySelector('.ql-picker-item[data-value="3"]');

    // Verify if the element exists before modifying it
    if (h3Button) {
      h3Button.setAttribute('data-label', 'Titre'); // Modifier l'étiquette
      h3Button.innerHTML = '';
    }
  }, []);

  return !isOpen ? null : (
    <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
      <Stack spacing={2} height="100%" justifyContent="space-between">
        <Stack spacing={2}>
          <Typography variant="body1" textAlign="center" fontWeight="bold">
            {t(faq ? 'updateFaq' : 'addFaq', { ns: 'field' })}
          </Typography>
          <TextField
            fullWidth
            id="question"
            name="question"
            label={upperFirst(t('word.question', { ns: 'common' }))}
            value={formik.values.question}
            onChange={formik.handleChange}
            error={formik.touched.question && Boolean(formik.errors.question)}
            helperText={formik.touched.question && formik.errors.question}
          />
          <ReactQuill
            theme="snow"
            value={formik.values.answer}
            onChange={(content) => formik.setFieldValue('answer', content)}
            placeholder={upperFirst(t('word.answer', { ns: 'common' }))}
            style={{ height: '200px', marginBottom: '40px' }}
          />
        </Stack>
        <DialogActions sx={{ width: '100%', px: 0, alignSelf: 'flex-end' }}>
          <Stack direction="row" spacing={2} py={3} width="100%" justifyContent="space-between">
            <Button
              onClick={handleClose}
              color="uncolored"
              variant="outlined"
              fullWidth={Device.isMobile}
              disabled={loading}
            >
              {t('cancel', { ns: 'cta' })}
            </Button>
            <Button
              loading={loading}
              fullWidth={Device.isMobile}
              type="submit"
              variant="contained"
              color="success"
              disabled={!(formik.isValid && formik.dirty)}
            >
              {t('save', { ns: 'cta' })}
            </Button>
          </Stack>
        </DialogActions>
      </Stack>
    </form>
  );
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
